// PDF-отчёт продаж: секции Кухня/Бар → категории → блюда.
import { Document, Font, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import type { SalesReport } from '@/services/admin';

Font.register({
  family: 'Inter',
  fonts: [
    { src: '/fonts/Inter-Regular.ttf' },
    { src: '/fonts/Inter-Bold.ttf', fontWeight: 700 },
  ],
});
// Без этого React-PDF переносит русские слова по английским правилам (п.19).
Font.registerHyphenationCallback((word) => [word]);

const C = {
  text: '#0f172a',
  muted: '#64748b',
  line: '#e2e8f0',
  headerBg: '#f1f5f9',
  brand: '#4f62e0',
  kitchen: '#f59e0b',
  bar: '#06b6d4',
};

const s = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 40,
    paddingHorizontal: 36,
    fontFamily: 'Inter',
    fontSize: 10,
    color: C.text,
    lineHeight: 1.4,
  },
  h1: { fontSize: 18, fontWeight: 700 },
  h2: { fontSize: 13, fontWeight: 700, marginTop: 14, marginBottom: 6 },
  h3: { fontSize: 11, fontWeight: 700, marginTop: 10, marginBottom: 4 },
  muted: { color: C.muted, fontSize: 9 },
  subtitle: { color: C.muted, fontSize: 10 },

  headerBlock: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: C.line,
    paddingBottom: 10,
    marginBottom: 10,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginVertical: 8,
  },
  statBox: {
    width: '31%',
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 6,
    padding: 8,
  },
  statLabel: { fontSize: 8, textTransform: 'uppercase', color: C.muted, marginBottom: 2 },
  statValue: { fontSize: 13, fontWeight: 700 },

  sectionTitle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginTop: 8,
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: C.headerBg,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 4,
    marginTop: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.line,
  },
  th: { fontWeight: 700, fontSize: 9, color: C.muted, textTransform: 'uppercase' },
  td: { fontSize: 9 },

  col1: { flex: 1 },
  col2: { flex: 2 },
  col3: { flex: 3 },
  col4: { flex: 4 },
  right: { textAlign: 'right' },

  footer: {
    position: 'absolute',
    left: 36,
    right: 36,
    bottom: 18,
    fontSize: 8,
    color: C.muted,
    borderTopWidth: 0.5,
    borderTopColor: C.line,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});

// Копейки → рубли: целые без дробной части, иначе ровно две цифры.
function formatRub(kop: number): string {
  const safe = Number.isFinite(kop) ? Math.round(kop) : 0;
  const hasKop = safe % 100 !== 0;
  const rub = (safe / 100).toLocaleString('ru-RU', {
    minimumFractionDigits: hasKop ? 2 : 0,
    maximumFractionDigits: hasKop ? 2 : 0,
  });
  return `${rub} ₽`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function SalesReportPdf({ report }: { report: SalesReport }) {
  const generatedAt = fmtDate(report.generatedAt);
  const establishmentName = report.establishment?.name ?? 'Resstro SAS';

  return (
    <Document title="Отчёт продаж" author="Resstro SAS">
      <Page size="A4" style={s.page}>
        <View style={s.headerBlock}>
          <View>
            <Text style={s.h1}>Отчёт по продажам</Text>
            <Text style={s.subtitle}>{establishmentName}</Text>
            <Text style={s.muted}>
              Период: {fmtDate(report.range.from)} — {fmtDate(report.range.to)}
            </Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={s.muted}>Сформирован: {generatedAt}</Text>
          </View>
        </View>

        {/* KPI */}
        <View style={s.statsGrid}>
          <Stat label="Заказы" value={String(report.totals.ordersCount)} />
          <Stat label="Блюд продано" value={String(report.totals.itemsQuantity)} />
          <Stat label="Выручка" value={formatRub(report.totals.revenue)} />
        </View>

        {report.sections.map((sec) => {
          const bg = sec.section === 'KITCHEN'
            ? '#fef3c7'
            : sec.section === 'BAR'
              ? '#cffafe'
              : '#d1fae5';
          return (
            <View key={sec.section} wrap>
              <View style={[s.sectionTitle, { backgroundColor: bg }]}>
                <Text style={{ fontWeight: 700, fontSize: 12 }}>{sec.sectionLabel}</Text>
                <Text style={{ fontWeight: 700 }}>
                  {sec.quantity} шт · {formatRub(sec.total)}
                </Text>
              </View>

              {sec.categories.map((cat) => (
                <View key={cat.categoryId ?? cat.categoryName} style={{ marginTop: 6 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={s.h3}>{cat.categoryName}</Text>
                    <Text style={{ fontSize: 10, fontWeight: 700, alignSelf: 'center' }}>
                      {cat.quantity} шт · {formatRub(cat.total)}
                    </Text>
                  </View>
                  <View style={s.tableHeader}>
                    <Text style={[s.th, s.col4]}>{' '}</Text>
                    <Text style={[s.th, s.col1, s.right]}>Кол-во</Text>
                    <Text style={[s.th, s.col2, s.right]}>Сумма</Text>
                  </View>
                  {cat.dishes.map((d) => (
                    <View key={d.dishId + d.section} style={s.tableRow}>
                      <Text style={[s.td, s.col4]}>{d.name}</Text>
                      <Text style={[s.td, s.col1, s.right]}>{d.quantity}</Text>
                      <Text style={[s.td, s.col2, s.right]}>{formatRub(d.total)}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          );
        })}

        <View style={s.footer} fixed>
          <Text>
            {establishmentName} · Отчёт продаж · Сформирован {generatedAt}
          </Text>
          <Text
            render={({ pageNumber, totalPages }) => `Страница ${pageNumber} из ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <View style={s.statBox}>
      <Text style={s.statLabel}>{label}</Text>
      <Text style={s.statValue}>{value}</Text>
    </View>
  );
}
